import { useState } from "react";

type FoldSide = "left" | "right" | "split";

interface Config {
  panels: number;
  label: string;
  widthRange: string;
  foldOptions: { label: string; side: FoldSide; split?: string }[];
  priceFrom: string;
  priceTo: string;
  popular?: boolean;
  description: string;
  openingWidth: string;
  bestFor: string;
}

const configs: Config[] = [
  {
    panels: 2,
    label: "2 Panel",
    widthRange: "1.2m – 2.4m",
    openingWidth: "Up to 2.4m",
    priceFrom: "£1,800",
    priceTo: "£3,200",
    description: "The most compact bifold configuration. Both panels fold neatly to one side, leaving the full opening clear. Ideal for smaller extensions, utility rooms, or replacing a standard patio door.",
    bestFor: "Small extensions, utility rooms, narrow openings",
    foldOptions: [
      { label: "Both fold left", side: "left" },
      { label: "Both fold right", side: "right" },
    ],
  },
  {
    panels: 3,
    label: "3 Panel",
    widthRange: "1.8m – 3.6m",
    openingWidth: "Up to 3.6m",
    priceFrom: "£2,400",
    priceTo: "£4,500",
    description: "The most popular configuration for typical kitchen extensions. Two panels fold to one side and one to the other, or all three to one side. Creates a generous opening with excellent traffic flow.",
    bestFor: "Kitchen extensions, dining rooms, typical garden access",
    popular: true,
    foldOptions: [
      { label: "2 left, 1 right", side: "split", split: "2-1" },
      { label: "All fold left", side: "left" },
      { label: "All fold right", side: "right" },
    ],
  },
  {
    panels: 4,
    label: "4 Panel",
    widthRange: "2.4m – 4.8m",
    openingWidth: "Up to 4.8m",
    priceFrom: "£3,200",
    priceTo: "£5,800",
    description: "A versatile configuration for medium-to-large openings. The classic 2-2 split creates a symmetrical look with a central traffic door. Alternatively, a 3-1 split maximises the open span on one side.",
    bestFor: "Open-plan living, medium extensions, symmetrical openings",
    foldOptions: [
      { label: "2-2 split (symmetrical)", side: "split", split: "2-2" },
      { label: "3 left, 1 right", side: "split", split: "3-1" },
      { label: "1 left, 3 right", side: "split", split: "1-3" },
    ],
  },
  {
    panels: 5,
    label: "5 Panel",
    widthRange: "3.0m – 6.0m",
    openingWidth: "Up to 6.0m",
    priceFrom: "£4,200",
    priceTo: "£7,200",
    description: "For wide openings that demand a spectacular result. A 5-panel set creates an almost completely open span, blurring the boundary between inside and outside. The 3-2 split is the most popular layout.",
    bestFor: "Large extensions, open-plan living, wide garden access",
    foldOptions: [
      { label: "3 left, 2 right", side: "split", split: "3-2" },
      { label: "2 left, 3 right", side: "split", split: "2-3" },
      { label: "All fold left", side: "left" },
    ],
  },
  {
    panels: 6,
    label: "6 Panel",
    widthRange: "3.6m – 7.2m",
    openingWidth: "Up to 7.2m",
    priceFrom: "£5,500",
    priceTo: "£8,500+",
    description: "The ultimate bifold configuration for truly spectacular openings. A 6-panel set transforms a room completely, creating a wall of glass that opens entirely. The 3-3 split is perfectly symmetrical and visually stunning.",
    bestFor: "Large homes, orangeries, premium extensions, wide spans",
    foldOptions: [
      { label: "3-3 split (symmetrical)", side: "split", split: "3-3" },
      { label: "4 left, 2 right", side: "split", split: "4-2" },
      { label: "All fold left", side: "left" },
    ],
  },
];

const colours = [
  { name: "Anthracite Grey", hex: "#3D3D3D", popular: true },
  { name: "Jet Black", hex: "#1A1A1A" },
  { name: "Pure White", hex: "#F5F5F5" },
  { name: "Cream", hex: "#F5F0E8" },
  { name: "Chartwell Green", hex: "#7A9E7E" },
  { name: "Slate Blue", hex: "#6B7FA3" },
  { name: "Agate Grey", hex: "#8A8D8F" },
  { name: "Bronze", hex: "#8B6914" },
];

function PanelDiagram({ panels, foldSide, split, colour }: { panels: number; foldSide: FoldSide; split?: string; colour: string }) {
  const panelWidth = Math.min(60, Math.floor(480 / panels));
  const totalWidth = panelWidth * panels;

  const getSplitLeft = () => {
    if (!split) return foldSide === "left" ? panels : 0;
    const parts = split.split("-").map(Number);
    return parts[0];
  };

  const leftCount = getSplitLeft();
  const rightCount = panels - leftCount;

  return (
    <div className="flex flex-col items-center">
      {/* Frame */}
      <div
        className="relative border-4 rounded-sm overflow-hidden bg-stone-100"
        style={{ width: totalWidth + 16, height: 160, borderColor: colour }}
      >
        {/* Floor line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-stone-300"></div>

        {/* Panels */}
        <div className="absolute inset-2 bottom-1 flex">
          {Array.from({ length: panels }).map((_, i) => {
            const isFolded =
              foldSide === "left"
                ? i < Math.floor(panels * 0.4)
                : foldSide === "right"
                ? i >= Math.ceil(panels * 0.6)
                : i < leftCount
                ? i < Math.floor(leftCount * 0.4)
                : i >= panels - Math.floor(rightCount * 0.4);

            return (
              <div
                key={i}
                className="flex-1 border-r last:border-r-0 relative flex items-center justify-center"
                style={{
                  borderColor: colour,
                  borderWidth: "0 1px 0 0",
                  background: isFolded ? `${colour}22` : "rgba(200,230,255,0.3)",
                  transform: isFolded ? "scaleX(0.6)" : "scaleX(1)",
                  transformOrigin: i < leftCount ? "left" : "right",
                  transition: "transform 0.3s ease",
                }}
              >
                {/* Glazing bars */}
                <div className="absolute inset-2 border" style={{ borderColor: `${colour}66` }}></div>
                <div className="absolute top-1/2 left-0 right-0 border-t" style={{ borderColor: `${colour}44` }}></div>
              </div>
            );
          })}
        </div>

        {/* Fold arrows */}
        {leftCount > 0 && (
          <div className="absolute top-2 left-2 text-xs" style={{ color: colour, fontFamily: "'Raleway', sans-serif", fontSize: 9 }}>
            ← folds
          </div>
        )}
        {rightCount > 0 && (
          <div className="absolute top-2 right-2 text-xs" style={{ color: colour, fontFamily: "'Raleway', sans-serif", fontSize: 9 }}>
            folds →
          </div>
        )}
      </div>

      {/* Width indicator */}
      <div className="flex items-center gap-1 mt-2" style={{ width: totalWidth + 16 }}>
        <div className="flex-1 border-t border-dashed border-stone-400"></div>
        <span className="text-zinc-400 text-xs px-1" style={{ fontFamily: "'Raleway', sans-serif" }}>{panels} panels</span>
        <div className="flex-1 border-t border-dashed border-stone-400"></div>
      </div>
    </div>
  );
}

export default function ConfigurationVisualiser() {
  const [selectedPanels, setSelectedPanels] = useState(3);
  const [selectedFoldIdx, setSelectedFoldIdx] = useState(0);
  const [selectedColour, setSelectedColour] = useState(colours[0]);

  const config = configs.find((c) => c.panels === selectedPanels)!;
  const foldOption = config.foldOptions[selectedFoldIdx] ?? config.foldOptions[0];

  const handlePanelChange = (panels: number) => {
    setSelectedPanels(panels);
    setSelectedFoldIdx(0);
  };

  return (
    <section className="py-20 bg-stone-50" id="configurations">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Configuration Builder</span>
          <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Choose Your Panel Configuration
          </h2>
          <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Bifold doors are available in 2 to 6 panel configurations. Select your panel count, fold direction, and frame colour to visualise your doors.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Left: Controls */}
          <div className="lg:col-span-2 space-y-7">
            {/* Panel count */}
            <div>
              <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>Number of Panels</p>
              <div className="grid grid-cols-5 gap-2">
                {configs.map((c) => (
                  <button
                    key={c.panels}
                    onClick={() => handlePanelChange(c.panels)}
                    className={`relative py-3 rounded-lg text-sm font-bold transition-all cursor-pointer border ${
                      selectedPanels === c.panels
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-600 border-stone-200 hover:border-zinc-400"
                    }`}
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {c.panels}
                    {c.popular && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded-full whitespace-nowrap">Popular</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Fold direction */}
            <div>
              <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>Fold Configuration</p>
              <div className="space-y-2">
                {config.foldOptions.map((opt, i) => (
                  <button
                    key={opt.label}
                    onClick={() => setSelectedFoldIdx(i)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-sm transition-all cursor-pointer text-left ${
                      selectedFoldIdx === i
                        ? "bg-amber-50 border-amber-300 text-amber-800"
                        : "bg-white border-stone-200 text-zinc-600 hover:border-stone-300"
                    }`}
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 rounded-full border-2 ${selectedFoldIdx === i ? "border-amber-500 bg-amber-500" : "border-stone-300"}`}>
                      {selectedFoldIdx === i && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                    </div>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Frame colour */}
            <div>
              <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>Frame Colour</p>
              <div className="grid grid-cols-4 gap-2">
                {colours.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setSelectedColour(c)}
                    title={c.name}
                    className={`relative w-full aspect-square rounded-lg border-2 transition-all cursor-pointer ${
                      selectedColour.name === c.name ? "border-amber-500 scale-110" : "border-transparent hover:border-stone-300"
                    }`}
                    style={{ backgroundColor: c.hex }}
                  >
                    {c.popular && (
                      <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                        <i className="ri-star-fill text-white" style={{ fontSize: 7 }}></i>
                      </span>
                    )}
                    {selectedColour.name === c.name && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <i className="ri-check-line text-white text-sm" style={{ textShadow: "0 0 3px rgba(0,0,0,0.8)" }}></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-zinc-500 text-xs mt-2" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Selected: <strong>{selectedColour.name}</strong> · 200+ RAL colours available
              </p>
            </div>
          </div>

          {/* Right: Preview + details */}
          <div className="lg:col-span-3">
            {/* Visual preview */}
            <div className="bg-white rounded-xl border border-stone-100 p-8 mb-6 flex flex-col items-center">
              <p className="text-xs text-zinc-400 uppercase tracking-wide mb-6" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Preview — {config.label} · {foldOption.label} · {selectedColour.name}
              </p>
              <PanelDiagram
                panels={config.panels}
                foldSide={foldOption.side}
                split={foldOption.split}
                colour={selectedColour.hex}
              />
              <div className="mt-6 flex items-center gap-6 text-center">
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Opening Width</p>
                  <p className="text-zinc-800 font-bold text-sm mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{config.openingWidth}</p>
                </div>
                <div className="w-px h-8 bg-stone-200"></div>
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Price Guide</p>
                  <p className="text-amber-700 font-bold text-sm mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{config.priceFrom} – {config.priceTo}</p>
                </div>
                <div className="w-px h-8 bg-stone-200"></div>
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Width Range</p>
                  <p className="text-zinc-800 font-bold text-sm mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{config.widthRange}</p>
                </div>
              </div>
            </div>

            {/* Config details */}
            <div className="bg-white rounded-xl border border-stone-100 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-zinc-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{config.label} Bifold Doors</h3>
                  <p className="text-zinc-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{config.widthRange} opening width</p>
                </div>
                {config.popular && (
                  <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>Most Popular</span>
                )}
              </div>
              <p className="text-zinc-500 text-sm leading-relaxed mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>{config.description}</p>
              <div className="flex items-center gap-2 flex-wrap mb-5">
                <span className="text-xs text-zinc-500 font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>Best for:</span>
                {config.bestFor.split(", ").map((tag) => (
                  <span key={tag} className="text-xs bg-stone-100 text-zinc-600 px-2.5 py-1 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>{tag}</span>
                ))}
              </div>
              <a
                href="#quote-form"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Get Quote for {config.label}
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></div>
              </a>
            </div>
          </div>
        </div>

        {/* All configs overview */}
        <div className="mt-14">
          <h3 className="text-xl font-bold text-zinc-800 mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>All Configuration Options at a Glance</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {configs.map((c) => (
              <button
                key={c.panels}
                onClick={() => handlePanelChange(c.panels)}
                className={`relative text-left p-5 rounded-xl border transition-all cursor-pointer ${
                  selectedPanels === c.panels
                    ? "border-amber-400 bg-amber-50"
                    : "border-stone-200 bg-white hover:border-stone-300"
                }`}
              >
                {c.popular && (
                  <span className="absolute -top-2.5 left-4 bg-amber-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>Most Popular</span>
                )}
                <p className="text-2xl font-bold text-zinc-800 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{c.panels}</p>
                <p className="text-xs font-bold text-zinc-600 mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>{c.label}</p>
                <p className="text-zinc-400 text-xs mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{c.widthRange}</p>
                <p className="text-amber-700 text-xs font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{c.priceFrom}+</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
